// you will need to MODIFY this and other files to make them work with command objects,
// instead of directly performing the actions
import React, { useEffect, useCallback, useContext, useState } from "react";

import ControlContext from "../../../contexts/control-context";
import { selectShadowId } from "../../../shared/util";
import ShapeRenderer from "./ShapeRenderer";

import interact from 'interactjs'

const SVGLayer = () => {
  const {
    currMode,
    currBorderColor,
    currBorderWidth,
    currFillColor,
    currFontSize,
    currFontFamily,
    shapes,
    shapesMap,
    addShape,
    updateShape,
    selectedShapeId,
    selectShape,
    addFrame,
    frames,
    framesMap,
    selectedFrameId,
    addText,
  } = useContext(ControlContext);

  // use useState to set elements in the React state directly
  // the first element of the list is the state value
  // the second element of the list is a function to update the state value in the future
  const [drawing, setDrawing] = useState(false);
  const [initPoint, setInitPoint] = useState({ x: undefined, y: undefined });
  const [currPoint, setCurrPoint] = useState({ x: undefined, y: undefined });

  const [dragging, setDragging] = useState(false);
  const [draggingShape, setDraggingShape] = useState(undefined);
  const [mouseDownPoint, setMouseDownPoint] = useState({
    x: undefined,
    y: undefined,
  });

  const [resizing, setResizing] = useState(false)

  interact('rect.selected, ellipse.selected')
    .resizable({
      // resize from all edges and corners
      edges: { left: true, right: true, bottom: true, top: true },
      invert: 'reposition',
      listeners: {
        move: (event) => {
          let bbox = event.target.getBBox()
          let x = bbox.x + event.deltaRect.left
          let y = bbox.y + event.deltaRect.top
          let width = event.rect.width
          let height = event.rect.height
          updateShape({
              initCoords:  {x: x,         y: y},
              finalCoords: {x: x + width, y: y + height},
          })
        },
        start: () => setResizing(true),
        end: () => setResizing(false),
      }})

  const handleMouseDown = (e) => {
    if (currMode !== "select") {
      if (currMode === "text" && selectedShapeId) {
        // check previously selected object & textinput
        addText(selectedShapeId)
      }
      // should create
      selectShape(undefined);
      setDrawing(true);
      setInitPoint({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
      setCurrPoint({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
      e.preventDefault();
    } else {
      if (selectedShapeId && shapesMap[selectedShapeId].type === "text") {
        addText(selectedShapeId)
      }
      // should select
      if (e.target.nodeName === "svg") {
        // deselect
        selectShape(undefined);
      } else {
        // select
        const targetId = e.target.id;
        selectShape(targetId);
        setDragging(true);
        setMouseDownPoint({
          x: e.nativeEvent.offsetX,
          y: e.nativeEvent.offsetY,
        });
        setDraggingShape(
          shapesMap[shapes.filter((shapeId) => shapeId === targetId)[0]]
        );
      }
    }
  };

  const handleMouseMove = (e) => {
    if (drawing) {
      setCurrPoint({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
    } else if (!resizing && dragging && draggingShape) {
      const deltaX = e.nativeEvent.offsetX - mouseDownPoint.x;
      const deltaY = e.nativeEvent.offsetY - mouseDownPoint.y;

      updateShape({
        initCoords: {
          x: draggingShape.initCoords.x + deltaX,
          y: draggingShape.initCoords.y + deltaY,
        },
        finalCoords: {
          x: draggingShape.finalCoords.x + deltaX,
          y: draggingShape.finalCoords.y + deltaY,
        },
      });
    }
  };

  const handleMouseUp = (e) => {
    if (currMode !== "select") {
      if (currMode === "text") {
        let id;
        id = addShape({
          type: currMode,
          visible: true,
          initCoords: initPoint,
          finalCoords: currPoint,
          borderColor: currBorderColor,
          borderWidth: currBorderWidth,
          fillColor: currFillColor,
          fontfamily: currFontFamily,
          fontSize: currFontSize,
          oldText: "",
          newText: "",
        });
        selectShape(id);
      } else if (!(initPoint.x === currPoint.x && initPoint.y === currPoint.y)) {
        // check if it's too small
        const threshold = 10;
        let shouldCreate = true;
        const deltaX = Math.abs(initPoint.x - currPoint.x);
        const deltaY = Math.abs(initPoint.y - currPoint.y);
        if (currMode === "line") {
          if (Math.sqrt(deltaX ** 2 + deltaY ** 2) < threshold) {
            shouldCreate = false;
          }
        } else {
          if (deltaX < threshold || deltaY < threshold) {
            shouldCreate = false;
          }
        }

        if (shouldCreate) {
          // create
          addShape({
            type: currMode,
            visible: true,
            initCoords: initPoint,
            finalCoords: currPoint,
            borderColor: currBorderColor,
            borderWidth: currBorderWidth,
            fillColor: currFillColor,
            frameInteraction: null
          });
          selectShape(undefined);
        }
      }
      setDrawing(false);
      setInitPoint({ x: undefined, y: undefined });
      setCurrPoint({ x: undefined, y: undefined });
    } else {
      if (dragging &&
          mouseDownPoint.x !== e.nativeEvent.offsetX &&
          mouseDownPoint.y !== e.nativeEvent.offsetY) {
        updateShape({
          initCoords: {
            x: draggingShape.initCoords.x,
            y: draggingShape.initCoords.y,
          },
          finalCoords: {
            x: draggingShape.finalCoords.x,
            y: draggingShape.finalCoords.y,
          },
        }, true);
      }
      setDragging(false);
      setDraggingShape(undefined);
      setMouseDownPoint({ x: undefined, y: undefined });
    }
  };

  // useCallback gives a memoized version of the callback that changes when one of its dependencies change
  // the first argument is the function that will be run
  // the second is the dependencies that the function relies on
  const escKeyDownHandler = useCallback(
    (e) => {
      if (e.key === "Escape") {
        // abort
        if (drawing) {
          setDrawing(false);
          setInitPoint({ x: undefined, y: undefined });
          setCurrPoint({ x: undefined, y: undefined });
        } else if (dragging) {
          updateShape({
            initCoords: {
              x: draggingShape.initCoords.x,
              y: draggingShape.initCoords.y,
            },
            finalCoords: {
              x: draggingShape.finalCoords.x,
              y: draggingShape.finalCoords.y,
            },
          });
          setDragging(false);
          setDraggingShape(undefined);
          setMouseDownPoint({ x: undefined, y: undefined });
        }
      }
    },
    [drawing, dragging, draggingShape, updateShape]
  );

  const enterTextHandler = (e) => {
    console.log(e.key, e.code, typeof(e.key), typeof(e.code));
    if ((currMode === "text" || currMode === "select") 
          && selectedShapeId && document.getElementById(selectedShapeId) !== null 
          && e.target.tagName != "INPUT") {
      console.log(shapesMap[selectedShapeId].oldText, shapesMap[selectedShapeId].newText);
      if (e.key === "Backspace") {
        shapesMap[selectedShapeId].newText = shapesMap[selectedShapeId].newText.slice(0, -1);
        var elements = document.getElementsByName(selectedShapeId);
        for (var i=0; i<elements.length; i++) {
          elements[i].innerHTML = elements[i].innerHTML.slice(0, -1);
        }
      } else if (e.key.length === 1 || e.code === "Space") {
        shapesMap[selectedShapeId].newText += e.key;
        elements = document.getElementsByName(selectedShapeId);
        for (i=0; i<elements.length; i++) {
          elements[i].innerHTML += e.key;
        }
      }
      console.log(shapesMap[selectedShapeId].oldText, shapesMap[selectedShapeId].newText);
    }
  };

  // useEffect will run after the render is committed to the screen
  // the first argument is the function that will run
  // the second argument are the dependencies, meaning this will only run when there is a change in these values
  useEffect(() => {
    window.addEventListener("keydown", escKeyDownHandler, true);
    window.addEventListener("keydown", enterTextHandler, true);
    return () => {
      window.removeEventListener("keydown", escKeyDownHandler, true);
      window.removeEventListener("keydown", enterTextHandler, true);
    }
  }, [escKeyDownHandler, enterTextHandler]);

  const renderTempShape = () => {
    if (
      initPoint.x !== undefined &&
      initPoint.y !== undefined &&
      currPoint.x !== undefined &&
      currPoint.y !== undefined
    ) {
      return new ShapeRenderer().forShape({
          type: currMode,
          initCoords: initPoint,
          finalCoords: currPoint,
          borderColor: currBorderColor,
          borderWidth: currBorderWidth,
          fillColor: currFillColor,
          visible: true
        }).render()
    }
  };

  const shapeRenderer = new ShapeRenderer()

  return (
    frames.filter((id) => framesMap[id] && framesMap[id].visible).length > 0 ?
    <svg
      id="workspace-svg"
      width="800"
      height="600"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <filter
        id={selectShadowId}
        x="-100%"
        y="-100%"
        width="400%"
        height="400%"
      >
        <feDropShadow
          dx="0"
          dy="0"
          stdDeviation="15"
          floodColor="rgba(0, 0, 0, 0.7)"
        />
      </filter>
      {shapes.map((shapeId, idx) => {
        return shapeRenderer.forShape(shapesMap[shapeId])
                .forFrame(selectedFrameId)
                .withKey(idx)
                .includeId()
                .withFilter(
                  selectedShapeId && selectedShapeId === shapeId
                    ? `url(#${selectShadowId})`
                    : null
                ).render()
      })}
      {drawing && renderTempShape()}
    </svg> :
    <button onClick={addFrame}>Create a New Frame Here</button>
  );
};

export default SVGLayer;
